import { useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';
import { resolveDisplayTimezone } from '@/lib/facilityTimezone';

/**
 * Resolved IANA timezone for the active staff user.
 *
 * Source order (mirrors legacy `legacyOffsetHours()` resolution that the
 * API runs server-side in `AccountTimezone::effectiveIanaTimezone`):
 *
 *   1. `staffUser.timezone` — set by the API from `users_timezones` when
 *      the user picked a specific zone (active=1, timezoneID > 0).
 *   2. Same field but populated from `aa_accounts.timezone` when the user
 *      picked "Account default" or has no row.
 *   3. Browser-resolved zone (`Intl.DateTimeFormat().resolvedOptions().timeZone`)
 *      as a last-resort safety net for legacy payloads that pre-date this
 *      field.
 *
 * Use this for every appointment/calendar formatting site so the user sees
 * times in their preferred zone regardless of where their browser is.
 */
export function useUserTimezone(): string {
  const user = useAuthStore((s) => s.user);
  return useMemo(() => resolveDisplayTimezone(user?.timezone), [user?.timezone]);
}
